import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

export default function UserMenu({ isDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted) setUser(data?.session?.user || null);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => {
      sub.subscription?.unsubscribe?.();
      mounted = false;
    };
  }, []);

  // Close on outside click or Escape
  useEffect(() => {
    const handleDocClick = (e) => {
      if (!open) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const avatarUrl = user?.user_metadata?.avatar_url || null;

  const goToSignIn = () => {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    setOpen(false);
    navigate(`/signin?return=${returnTo}`);
  };

  const goToAccount = () => {
    if (!user) {
      goToSignIn();
    } else {
      setOpen(false);
      navigate('/account');
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('Sign out failed:', error.message);
      }
      setUser(null);
      setOpen(false);
      // If currently on the account page, redirect to sign-in
      if (location.pathname.startsWith('/account')) {
        const returnTo = encodeURIComponent('/');
        navigate(`/signin?return=${returnTo}`, { replace: true });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Sign out threw:', e?.message);
      setUser(null);
      setOpen(false);
      if (location.pathname.startsWith('/account')) {
        const returnTo = encodeURIComponent('/');
        navigate(`/signin?return=${returnTo}`, { replace: true });
      }
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden transition-all flex items-center justify-center ${
          user
            ? 'bg-blue-600 text-white hover:brightness-110'
            : isDarkMode
              ? 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200'
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={user?.email || 'Account'}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="User avatar" className="w-full h-full object-cover" />
        ) : (
          // Default avatar: outline when logged out, filled when logged in
          user ? (
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
              <path fillRule="evenodd" d="M12 3.75a4.5 4.5 0 100 9 4.5 4.5 0 000-9zM3.75 20.25a8.25 8.25 0 1116.5 0h-16.5z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
            </svg>
          )
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50 ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <button
            role="menuitem"
            className={`w-full text-left px-3 py-2 text-sm rounded-t-lg ${
              isDarkMode
                ? 'text-gray-200 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-100'
            } flex items-center gap-2`}
            onClick={goToAccount}
          >
            <UserIcon className="w-4 h-4" />
            My Account
          </button>

          {!user ? (
            <button
              role="menuitem"
              className={`w-full text-left px-3 py-2 text-sm rounded-b-lg ${
                isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={goToSignIn}
            >
              Sign In
            </button>
          ) : (
            <button
              role="menuitem"
              className={`w-full text-left px-3 py-2 text-sm rounded-b-lg ${
                isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              } flex items-center gap-2`}
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          )}
        </div>
      )}
    </div>
  );
}
